export const SSO_ATTEMPT_KEY = "sso_attempt";
export const SSO_STATE_KEY = "sso_state";
export const SSO_RETURN_KEY = "sso_return";
export const SSO_CALLBACK_CACHE_KEY = "sso_callback_cache";
export const SSO_VERIFIER_KEY = "sso_code_verifier";
export const SSO_CLIENT_ENTITY = "entity";

export function createSsoState() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function ssoRedirectUri() {
  return `${window.location.origin}/sso/callback`;
}

function bytesToBase64Url(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function createSsoPkce() {
  const random = new Uint8Array(32);
  window.crypto.getRandomValues(random);
  const verifier = bytesToBase64Url(random);
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return {
    verifier,
    challenge: bytesToBase64Url(new Uint8Array(digest)),
    method: "S256",
  };
}

export function buildSsoAuthorizeUrl({
  authURL,
  clientId,
  redirectUri,
  state,
  tenant,
  codeChallenge,
  codeChallengeMethod,
  costCenterId,
  hash,
}) {
  const url = new URL(`${String(authURL).replace(/\/$/, "")}/authorize`);
  url.searchParams.set("clientId", clientId);
  url.searchParams.set("redirectUri", redirectUri);
  url.searchParams.set("state", state);
  url.searchParams.set("codeChallenge", codeChallenge);
  url.searchParams.set("codeChallengeMethod", codeChallengeMethod);
  if (tenant) {
    url.searchParams.set("tenant", tenant);
  }
  if (costCenterId) {
    url.searchParams.set("costCenterId", String(costCenterId));
  }
  if (hash) {
    url.searchParams.set("hash", String(hash));
  }
  return url.toString();
}

/** Lê hash_link_senha da query (?hash=) ou do path legado /login/{hash}/hash. */
export function readHashFromLocation(location = window.location) {
  try {
    const params = new URLSearchParams(location.search || "");
    const fromQuery = params.get("hash");
    if (fromQuery) {
      return fromQuery;
    }
    const parts = String(location.pathname || "")
      .split("/")
      .filter(Boolean);
    const hashIdx = parts.indexOf("hash");
    if (hashIdx > 0 && parts[hashIdx - 1]) {
      return parts[hashIdx - 1];
    }
  } catch (error) {
    return "";
  }
  return "";
}

/** Remove hash da query sem recarregar a página. */
export function stripHashFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("hash")) {
      return;
    }
    url.searchParams.delete("hash");
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, document.title, next);
  } catch (error) {
    return;
  }
}

export function pathWithoutHash(path) {
  if (!path) {
    return "/";
  }
  try {
    const url = new URL(path, window.location.origin);
    url.searchParams.delete("hash");
    return `${url.pathname}${url.search}` || "/";
  } catch (error) {
    return String(path).replace(/([?&])hash=[^&]*&?/g, "$1").replace(/[?&]$/, "") || "/";
  }
}

export function buildSsoLogoutUrl({ authURL, postLogoutRedirectUri, tenant }) {
  const url = new URL(`${String(authURL).replace(/\/$/, "")}/logout`);
  if (postLogoutRedirectUri) {
    url.searchParams.set("postLogoutRedirectUri", postLogoutRedirectUri);
  }
  if (tenant) {
    url.searchParams.set("tenant", tenant);
  }
  return url.toString();
}

export function buildSsoSessionUrl({ authURL, tenant }) {
  const url = new URL(`${String(authURL).replace(/\/$/, "")}/api/sso/session`);
  if (tenant) {
    url.searchParams.set("tenant", tenant);
  }
  return url.toString();
}

export function hasSsoAttempt() {
  try {
    return window.sessionStorage.getItem(SSO_ATTEMPT_KEY) === "1";
  } catch (error) {
    return false;
  }
}

export function clearSsoAttempt() {
  try {
    window.sessionStorage.removeItem(SSO_ATTEMPT_KEY);
  } catch (error) {
    return;
  }
}

export async function redirectToSsoAuthorize({
  authURL,
  clientId,
  tenant,
  returnPath,
  costCenterId,
  hash,
}) {
  const state = createSsoState();
  const redirectUri = ssoRedirectUri();
  const pkce = await createSsoPkce();
  const safeReturn = pathWithoutHash(returnPath || "/");
  window.sessionStorage.setItem(SSO_STATE_KEY, state);
  window.sessionStorage.setItem(SSO_RETURN_KEY, safeReturn);
  window.sessionStorage.setItem(SSO_ATTEMPT_KEY, "1");
  window.sessionStorage.setItem(SSO_VERIFIER_KEY, pkce.verifier);
  window.location.assign(
    buildSsoAuthorizeUrl({
      authURL,
      clientId,
      redirectUri,
      state,
      tenant,
      codeChallenge: pkce.challenge,
      codeChallengeMethod: pkce.method,
      costCenterId,
      hash,
    })
  );
}

export function peekSsoCallback() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code") || "";
  const state = params.get("state") || "";
  let expected = "";
  let returnPath = "/";
  let codeVerifier = "";
  try {
    const cached = window.sessionStorage.getItem(SSO_CALLBACK_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
    expected = window.sessionStorage.getItem(SSO_STATE_KEY) || "";
    returnPath = window.sessionStorage.getItem(SSO_RETURN_KEY) || "/";
    codeVerifier = window.sessionStorage.getItem(SSO_VERIFIER_KEY) || "";
  } catch (error) {
    return { ok: false, returnPath: "/" };
  }

  if (!code || !state || state !== expected || !codeVerifier) {
    return { ok: false, returnPath };
  }

  const payload = { ok: true, code, returnPath, codeVerifier };
  try {
    window.sessionStorage.setItem(SSO_CALLBACK_CACHE_KEY, JSON.stringify(payload));
    window.sessionStorage.removeItem(SSO_STATE_KEY);
    window.sessionStorage.removeItem(SSO_RETURN_KEY);
    window.sessionStorage.removeItem(SSO_ATTEMPT_KEY);
    window.sessionStorage.removeItem(SSO_VERIFIER_KEY);
  } catch (error) {
    return payload;
  }

  return payload;
}

export function clearSsoCallbackCache() {
  try {
    window.sessionStorage.removeItem(SSO_CALLBACK_CACHE_KEY);
  } catch (error) {
    return;
  }
}

export function consumeSsoCallback() {
  return peekSsoCallback();
}

export function mapSsoTokenToEntityUser(payload) {
  return {
    data: {
      id: payload.personId,
      nome: payload.name,
      email: payload.email,
      hash_link_senha: payload.hashLinkPassword,
    },
    token: payload.accessToken,
    access_token: payload.accessToken,
  };
}
